import type { CountryProfile } from './countryProfiles';

/**
 * Landing-page prices. Naira is the source of truth and Nigeria sees it
 * unchanged. Every other country sees the USD price — the Naira price at
 * NGN_PER_USD — converted to its own currency at the static USD_RATES below.
 * Neither rate is live: review both when prices or exchange rates move, and
 * the page says that non-Naira prices are approximate.
 */
export const NGN_PER_USD = 1500;

/** Units of each currency per 1 USD. Last reviewed 2026-10-20. */
export const USD_RATES: Record<string, number> = {
  USD: 1,
  GHS: 12.2,
  KES: 129,
  ZAR: 17.8,
  UGX: 3650,
  TZS: 2550,
  RWF: 1430,
  XAF: 575,
  XOF: 575,
  ZMW: 26,
  EGP: 48.5,
  GBP: 0.76,
  EUR: 0.88,
  CAD: 1.38,
  INR: 86,
  AED: 3.67,
};

export interface PriceBand {
  range: string;
  /** Naira per student per academic calendar. */
  ngn: number;
}

export const PRIMARY_SECONDARY_BANDS: readonly PriceBand[] = [
  { range: '0 – 200 students', ngn: 4000 },
  { range: '201 – 500 students', ngn: 3500 },
  { range: '501 – 1,000 students', ngn: 3000 },
  { range: '1,001+ students', ngn: 2000 },
];

export const STUDENT_BANDS = PRIMARY_SECONDARY_BANDS.map((band) => band.range);

/** "From" prices per student per academic calendar, in Naira. */
export const STARTING_PRICES_NGN = {
  primarySecondaryMin: 2000,
  primarySecondaryMax: 4000,
  tertiary: 4000,
  other: 3000,
} as const;

export interface ProctoringTier {
  range: string;
  monthlyNgn: number;
  highlight?: boolean;
}

export const PROCTORING_TIERS: readonly ProctoringTier[] = [
  { range: '1 – 499 Students', monthlyNgn: 315000 },
  { range: '500 – 999 Students', monthlyNgn: 441000 },
  { range: '1,000 – 2,999 Students', monthlyNgn: 819000, highlight: true },
  { range: '3,000 – 4,999 Students', monthlyNgn: 1512000 },
];

/** Hero-mock sample tuition, in Naira. */
export const SAMPLE_TUITION_NGN = 240000;

export interface PriceContext {
  currency: string;
  locale: string;
  /** False when the amount is a conversion from Naira, not a set price. */
  isReferencePrice: boolean;
}

/**
 * The currency a visitor is shown. A profiled country whose currency has no
 * rate falls back to USD rather than inventing a conversion.
 */
export function priceContext(profile: CountryProfile): PriceContext {
  if (profile.currency === 'NGN') return { currency: 'NGN', locale: 'en-NG', isReferencePrice: true };
  if (USD_RATES[profile.currency] !== undefined) {
    return { currency: profile.currency, locale: profile.locale, isReferencePrice: false };
  }
  return { currency: 'USD', locale: 'en-US', isReferencePrice: false };
}

/** Round to 3 significant figures, but never below whole units above 100. */
function roundForDisplay(amount: number): number {
  if (amount >= 100) {
    const magnitude = 10 ** Math.max(0, Math.floor(Math.log10(amount)) - 2);
    return Math.round(amount / magnitude) * magnitude;
  }
  return Math.round(amount * 100) / 100;
}

/** Naira amount converted to the context's currency. */
export function convertFromNgn(ngn: number, context: PriceContext): number {
  if (context.currency === 'NGN') return ngn;
  const usd = ngn / NGN_PER_USD;
  return roundForDisplay(usd * (USD_RATES[context.currency] ?? 1));
}

export function formatPrice(ngn: number, context: PriceContext): string {
  const amount = convertFromNgn(ngn, context);
  return new Intl.NumberFormat(context.locale, {
    style: 'currency',
    currency: context.currency,
    maximumFractionDigits: amount < 100 && !Number.isInteger(amount) ? 2 : 0,
    minimumFractionDigits: amount < 100 && !Number.isInteger(amount) ? 2 : 0,
  }).format(amount);
}

/** Short form for dashboard mocks: ₦184M, $123K, KSh 15.9M. */
export function formatCompactPrice(ngn: number, context: PriceContext): string {
  return new Intl.NumberFormat(context.locale, {
    style: 'currency',
    currency: context.currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(convertFromNgn(ngn, context));
}
